import { useMemo } from 'react';
import { Bot } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { AgentSummary } from 'features/chat/agent-chat-module/types/agent.service.type';
import { useGetAgents } from 'features/chat/hooks/use-agents';
import { getAskAgentOwnerToAddMessage } from './ask-agent-owner-message';
import { AgentConfigSubagentCard } from './agent-config-subagent-card';

type SubagentsSectionProps = {
  enabled: boolean;
  canEdit?: boolean;
  projectKey: string;
  selectedSubagentIds: string[];
  onRemoveSubagent: (subagentId: string) => void;
};

export function SubagentsSection({
  enabled,
  canEdit = true,
  projectKey,
  selectedSubagentIds,
  onRemoveSubagent,
}: SubagentsSectionProps) {
  const { t } = useTranslation();
  const { data: agentsData } = useGetAgents(
    {
      limit: 100,
      offset: 0,
      project_key: projectKey,
    },
    { enabled: !!projectKey && selectedSubagentIds.length > 0, scope: 'subagents-config' }
  );

  const agentsById = useMemo(() => {
    const map = new Map<string, AgentSummary>();
    for (const agent of agentsData?.agents ?? []) {
      map.set(agent.id, agent);
    }
    return map;
  }, [agentsData?.agents]);

  const selectedSubagents = useMemo(
    () =>
      selectedSubagentIds.map((id) => {
        const agent = agentsById.get(id);
        if (agent) return agent;

        return {
          id,
          name: id,
          tags: [],
          role: '',
          description: '',
          logo_url: null,
          logo_id: null,
          is_archived: false,
          is_disabled: false,
          widget_id: '',
          visibility: 'private',
        } satisfies AgentSummary;
      }),
    [selectedSubagentIds, agentsById]
  );

  return (
    <div className="space-y-4">
      {selectedSubagents.length > 0 ? (
        <div className="grid gap-4">
          {selectedSubagents.map((agent) => (
            <AgentConfigSubagentCard
              key={agent.id}
              agent={agent}
              onRemove={onRemoveSubagent}
              disabled={!enabled}
            />
          ))}
        </div>
      ) : (
        <div
          className={`flex h-[141px] flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-[#e7e7e7] bg-[#f6f6f6] text-center dark:border-border dark:bg-muted/30 ${!enabled ? 'opacity-50' : ''}`}
        >
          <Bot className="size-8 text-[#6d6d6d] dark:text-muted-foreground" strokeWidth={1.5} />
          <p className="max-w-[400px] text-xs font-medium leading-[1.6] text-[#6d6d6d] dark:text-muted-foreground">
            {canEdit
              ? t('NO_SUBAGENTS_ADDED_CLICK_ADD_TO_GET_STARTED') ||
              "No subagents added yet. Click 'Add' to get started."
              : getAskAgentOwnerToAddMessage(t, t('SUBAGENTS') || 'subagents')}
          </p>
        </div>
      )}
    </div>
  );
}
